import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ExplorerLivestreamProgramViewerShapeSeries } from "../../services/api";
import {
  distinctExplorerAccents,
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  formatViewerIntegerFloor,
} from "./utils";
import {
  formatProgramViewerShapeMinute,
  shapeAxisTicks,
} from "./LivestreamProgramViewerShapeChart";
import {
  VIEWER_SHAPE_AVERAGE_KEY,
  buildViewerShapeChartRows,
} from "./viewerShapeChartData";

const TOOLTIP_ROW_LIMIT = 12;

type TooltipEntry = {
  dataKey?: string | number;
  name?: string;
  value?: number | string | null;
  color?: string;
};

function clipLabel(value: string, max = 64): string {
  const text = value.replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function ShapeTooltip({
  active,
  label,
  payload,
  minuteLabel,
  moreLabel,
}: {
  active?: boolean;
  label?: unknown;
  payload?: TooltipEntry[];
  minuteLabel: (minute: number) => string;
  moreLabel: (count: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const minute = typeof label === "number" ? label : Number(label);
  const rows = payload
    .filter((entry) => entry.value != null && Number.isFinite(Number(entry.value)))
    .sort((left, right) => Number(right.value) - Number(left.value));
  if (rows.length === 0) return null;
  const shown = rows.slice(0, TOOLTIP_ROW_LIMIT);
  const extra = rows.length - shown.length;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        maxWidth: 360,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {minuteLabel(Number.isFinite(minute) ? minute : 0)}
      </Typography>
      {shown.map((entry) => (
        <Stack
          key={String(entry.dataKey)}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ py: 0.125 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {entry.name}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {formatViewerIntegerFloor(Number(entry.value))}
          </Typography>
        </Stack>
      ))}
      {extra > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {moreLabel(extra)}
        </Typography>
      ) : null}
    </Box>
  );
}

export type LivestreamProgramViewerShapeDialogProps = {
  open: boolean;
  title: string;
  hint: string;
  points: Array<{ minute: number; avgViewers: number }>;
  series: ExplorerLivestreamProgramViewerShapeSeries[];
  onClose: () => void;
};

export const LivestreamProgramViewerShapeDialog: React.FC<
  LivestreamProgramViewerShapeDialogProps
> = ({ open, title, hint, points, series, onClose }) => {
  const { t } = useTranslation("explorer");
  const theme = useTheme();
  const averageColor = theme.palette.text.primary;
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string> | null>(null);
  const [showAverage, setShowAverage] = useState(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const seriesKey = series.map((item) => item.livestreamId).join("|");
  useEffect(() => {
    setSelectedIds(null);
    setQuery("");
    setHoveredId(null);
  }, [seriesKey]);

  const colors = useMemo(
    () => distinctExplorerAccents(series.length),
    [series.length],
  );
  const colorById = useMemo(() => {
    const map = new Map<string, string>();
    series.forEach((item, index) => {
      map.set(item.livestreamId, colors[index] ?? colors[0] ?? averageColor);
    });
    return map;
  }, [series, colors, averageColor]);

  const selected = useMemo(() => {
    if (selectedIds) return selectedIds;
    return new Set(series.map((item) => item.livestreamId));
  }, [selectedIds, series]);

  const rows = useMemo(
    () =>
      buildViewerShapeChartRows(
        points,
        series.map((item) => ({ id: item.livestreamId, points: item.points })),
      ),
    [points, series],
  );
  const maxMinute = rows.length > 0 ? rows[rows.length - 1]!.minute : 0;

  const needle = query.trim().toLowerCase();
  const visibleSeries = series.filter((item) => {
    if (!needle) return true;
    const date = formatExplorerTableDateLine(item.startedAt).toLowerCase();
    const time = formatExplorerTableTimeLine(item.startedAt).toLowerCase();
    const label = (item.title || t("noTitle")).toLowerCase();
    return label.includes(needle) || date.includes(needle) || time.includes(needle);
  });

  const lines = useMemo(() => {
    const next: Array<{ id: string; name: string; color: string; width: number }> = [];
    if (showAverage) {
      next.push({
        id: VIEWER_SHAPE_AVERAGE_KEY,
        name: t("average"),
        color: averageColor,
        width: 2.5,
      });
    }
    for (const item of series) {
      if (!selected.has(item.livestreamId)) continue;
      const date = formatExplorerTableDateLine(item.startedAt);
      const time = formatExplorerTableTimeLine(item.startedAt);
      const name = item.title?.trim() || t("noTitle");
      next.push({
        id: item.livestreamId,
        name: clipLabel(`${date} · ${time} · ${name}`),
        color: colorById.get(item.livestreamId) ?? averageColor,
        width: 1.6,
      });
    }
    return next;
  }, [showAverage, series, selected, t, averageColor, colorById]);

  const setVisibleSelected = (on: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current ?? series.map((item) => item.livestreamId));
      for (const item of visibleSeries) {
        if (on) next.add(item.livestreamId);
        else next.delete(item.livestreamId);
      }
      return next;
    });
  };

  const toggle = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current ?? series.map((item) => item.livestreamId));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-label={title}
      PaperProps={{
        sx: {
          width: "min(1680px, 96vw)",
          height: "min(880px, 92vh)",
          maxHeight: "92vh",
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ pr: 1.5, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hint}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={t("viewerShapeClose")}
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
            gridTemplateRows: { xs: "minmax(280px, 1fr) minmax(220px, 0.85fr)", md: "minmax(0, 1fr)" },
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: 0, p: 2, display: "flex" }}>
            {lines.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ m: "auto" }}>
                {t("viewerShapePickOne")}
              </Typography>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 8 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={alpha(theme.palette.divider, 0.15)}
                      vertical={false}
                    />
                    <XAxis
                      type="number"
                      dataKey="minute"
                      domain={[0, Math.max(maxMinute, 0)]}
                      ticks={shapeAxisTicks(maxMinute)}
                      tickFormatter={(value) =>
                        formatProgramViewerShapeMinute(value as number)
                      }
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                      allowDecimals={false}
                      tickFormatter={(value) => formatViewerIntegerFloor(value as number)}
                    />
                    <RechartsTooltip
                      cursor={{ stroke: alpha(theme.palette.divider, 0.4) }}
                      content={(props) => (
                        <ShapeTooltip
                          active={props.active}
                          label={props.label}
                          payload={props.payload as TooltipEntry[] | undefined}
                          minuteLabel={formatProgramViewerShapeMinute}
                          moreLabel={(count) => t("viewerShapeMore", { count })}
                        />
                      )}
                    />
                    {lines.map((line) => {
                      const dimmed = hoveredId != null && hoveredId !== line.id;
                      return (
                        <Line
                          key={line.id}
                          type="monotone"
                          dataKey={line.id}
                          name={line.name}
                          stroke={line.color}
                          strokeWidth={
                            hoveredId === line.id ? Math.max(line.width, 2.5) : line.width
                          }
                          strokeOpacity={dimmed ? 0.2 : 1}
                          dot={false}
                          connectNulls={false}
                          isAnimationActive={false}
                          activeDot={{ r: 3 }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Stack
            sx={{
              minWidth: 0,
              minHeight: 0,
              borderTop: { xs: "1px solid", md: "none" },
              borderLeft: { xs: "none", md: "1px solid" },
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
              <ListItemButton
                onClick={() => setShowAverage((value) => !value)}
                onMouseEnter={() => {
                  if (showAverage) setHoveredId(VIEWER_SHAPE_AVERAGE_KEY);
                }}
                onMouseLeave={() => setHoveredId(null)}
                sx={{ borderRadius: 1, py: 0.5, px: 1 }}
              >
                <Checkbox
                  size="small"
                  checked={showAverage}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.5, mr: 0.5, pointerEvents: "none" }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: averageColor,
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {t("average")}
                </Typography>
              </ListItemButton>
              <TextField
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("viewerShapeSearch")}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("viewerShapeShowing", {
                    selected: selected.size,
                    total: series.length,
                  })}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => setVisibleSelected(true)}>
                    {t("viewerShapeSelectAll")}
                  </Button>
                  <Button size="small" onClick={() => setVisibleSelected(false)}>
                    {t("viewerShapeSelectNone")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
            <List sx={{ overflow: "auto", flex: 1, py: 0, px: 1, pb: 1.5 }}>
              {visibleSeries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                  {t("noData")}
                </Typography>
              ) : (
                visibleSeries.map((item) => {
                  const checked = selected.has(item.livestreamId);
                  const color = colorById.get(item.livestreamId) ?? averageColor;
                  const name = item.title?.trim() || t("noTitle");
                  return (
                    <ListItemButton
                      key={item.livestreamId}
                      onClick={() => toggle(item.livestreamId)}
                      onMouseEnter={() => {
                        if (checked) setHoveredId(item.livestreamId);
                      }}
                      onMouseLeave={() => setHoveredId(null)}
                      sx={{
                        alignItems: "flex-start",
                        borderRadius: 1,
                        py: 0.75,
                        px: 1,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        sx={{ p: 0.5, mr: 0.5, pointerEvents: "none" }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: color,
                          mt: 0.85,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatExplorerTableDateLine(item.startedAt)}
                          {" · "}
                          {formatExplorerTableTimeLine(item.startedAt)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
